import { forwardRef } from 'react'
import picomatch from 'picomatch'
import {
  NavLink as ReactRouterLink,
  NavLinkProps as ReactRouterLinkProps,
  useLocation,
} from 'react-router-dom'

import { cx } from 'styled-system/css'
import { Link as UiLink, LinkProps as UiLinkProps } from 'ui-kit/link'

const reactRouterProps = [
  'replace',
  'state',
  'to',
  'end',
  'preventScrollReset',
  'relative',
  'viewTransition',
  'caseSensitive',
] as const

type RRPropsKeys = (typeof reactRouterProps)[number]
type RRProps = Pick<ReactRouterLinkProps, RRPropsKeys>

export type LinkProps = Omit<UiLinkProps, 'end' | 'asChild'> &
  RRProps & {
    activeRoutes?: string[]
  }

// Same as used into NavLink component from react-router-dom
const ACTIVE_ROUTE_CLASSNAME = 'active'

export const Link = forwardRef<HTMLAnchorElement, LinkProps>(
  ({ children, to, className, activeRoutes = [], ...props }, ref) => {
    const [styleProps, linkProps] = splitLinkProps(props)
    const isRouteActive = useIsAnyRouteActive(activeRoutes)

    return (
      <UiLink asChild {...styleProps}>
        <ReactRouterLink
          to={to}
          className={cx(isRouteActive && ACTIVE_ROUTE_CLASSNAME, className)}
          {...linkProps}
          ref={ref}
        >
          {children}
        </ReactRouterLink>
      </UiLink>
    )
  },
)

function useIsAnyRouteActive(routes: string[]) {
  const location = useLocation()
  const safeLocation = getSafeRoute(location.pathname)
  const safeRoutes = routes.map(getSafeRoute)
  return picomatch.isMatch(safeLocation, safeRoutes)
}

function splitLinkProps(
  props: Partial<RRProps> & UiLinkProps,
): [styleProps: UiLinkProps, linkProps: Partial<RRProps>] {
  const styleProps = {}
  const linkProps = {}

  for (const key in props) {
    if (reactRouterProps.includes(key as any)) {
      linkProps[key] = props[key]
    } else {
      styleProps[key] = props[key]
    }
  }

  return [styleProps, linkProps]
}

function getSafeRoute(pathname: string) {
  return pathname === '/' ? pathname : pathname.replace(/\/$/, '')
}
